import { IEncryptedPayload } from '../models/common-models';

const SERVER_PUBLIC_KEY = {
  stage: {
    kty: 'EC',
    crv: 'P-256',
    // eslint-disable-next-line id-length
    x: 'gtbCcu6gWFaXXFDashCYP-lcYoNpsQjWM33l8fttFwo',
    // eslint-disable-next-line id-length
    y: 'HIMO1VmfWCJ2fKgGD7LAsrOtQNFR3Lzgn46o4lj5ug4'
  },
  prod: {
    kty: 'EC',
    crv: 'P-256',
    // eslint-disable-next-line id-length
    x: 'zp7R9ytOH_vY0614D47CBK5DSRM9jmaRPfLI-D8eVbg',
    // eslint-disable-next-line id-length
    y: 'VvVNYAqShdezc9Z2K1ZrakYKj0jVOGWQK_NIlamTI4U'
  }
};

const getServerPublicKey = () => {
  if (typeof window !== 'undefined') {
    if (window.location.hostname.includes('homedepot.com')) {
      return SERVER_PUBLIC_KEY.prod;
    }
    if (window.location.hostname.includes('qa74')) {
      return SERVER_PUBLIC_KEY.stage;
    }
    return '';
  }
  return '';
};

function base64ToBytes(base64: ArrayBuffer) {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}

export async function generateKeyPair() {
  if (!window || !window.crypto || !window.crypto.subtle) {
    return null;
  }
  const keyPair = await window.crypto.subtle.generateKey(
    {
      name: 'ECDH',
      namedCurve: 'P-256'
    },
    true,
    ['deriveKey', 'deriveBits']
  );

  const publicJwk = await window.crypto.subtle.exportKey('jwk', keyPair.publicKey);
  const privateJwk = await window.crypto.subtle.exportKey('jwk', keyPair.privateKey);

  return { publicJwk, privateJwk };
}

export async function decrypt(clientPrivateJwk: CryptoKey, encryptedPayload: IEncryptedPayload) {
  if (!window || !window.crypto || !window.crypto.subtle || !encryptedPayload) {
    return null;
  }
  try {
    const { cipherText, iv } = encryptedPayload;
    const importedClientPrivateKey = await window.crypto.subtle.importKey(
      'jwk',
      clientPrivateJwk,
      { name: 'ECDH', namedCurve: 'P-256' },
      false,
      ['deriveBits']
    );
    const serverKey = getServerPublicKey();
    const importedServerKey = await window.crypto.subtle.importKey(
      'jwk',
      serverKey,
      { name: 'ECDH', namedCurve: 'P-256' },
      false,
      []
    );

    const sharedSecret = await window.crypto.subtle.deriveBits(
      {
        name: 'ECDH',
        public: importedServerKey
      },
      importedClientPrivateKey,
      256
    );

    const hashedSecret = await window.crypto.subtle.digest('SHA-256', sharedSecret);

    const aesKey = await window.crypto.subtle.importKey(
      'raw',
      hashedSecret,
      { name: 'AES-GCM' },
      false,
      ['decrypt']
    );

    // Decrypt
    const decrypted = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: base64ToBytes(iv)
      },
      aesKey,
      base64ToBytes(cipherText)
    );

    const result = new TextDecoder().decode(decrypted);
    return JSON.parse(result);
  } catch (err) {
    console.error('Decryption error:', err);
    return null;
  }
}
